import { useMemo, useState } from 'react';
import { useRecentActivity } from '../lib/queries';
import { cn, formatTimestamp } from '../lib/utils';
import type { ActivityLogEntry } from '../lib/database.types';

export function ActivityLogPage() {
    const [limit, setLimit] = useState(100);
    const [filter, setFilter] = useState<string>('');
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const { data: entries = [], isLoading, error } = useRecentActivity(limit);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter((e) => {
            const hay = [e.actor_email, e.action, e.entity_type, JSON.stringify(e.new_value ?? {})]
                .join(' ')
                .toLowerCase();
            return hay.includes(q);
        });
    }, [entries, filter]);

    function toggleExpanded(id: string) {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
            <div className="mb-6">
                <div className="flex items-baseline gap-3 mb-1">
                    <h1 className="font-display font-bold text-2xl text-graphite-900 dark:text-graphite-50">Activity</h1>
                    <span className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                        {filtered.length} of {entries.length}
                    </span>
                </div>
                <p className="text-sm text-graphite-600 dark:text-graphite-400">
                    Every change to jobs, stages, flatwork, and weather holds. Append-only record.
                </p>
            </div>

            <div className="mb-4 flex flex-col sm:flex-row gap-3">
                <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter by actor, action, or entity..."
                    className="flex-1 bg-graphite-50 border border-graphite-300 text-graphite-900 placeholder-graphite-400 dark:bg-graphite-950 dark:border-graphite-700 dark:text-graphite-50 dark:placeholder-graphite-600 rounded px-3 py-2 text-sm focus:border-signal-500 focus:outline-none"
                />
                <select
                    value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))}
                    className="bg-graphite-50 border border-graphite-300 text-graphite-900 dark:bg-graphite-950 dark:border-graphite-700 dark:text-graphite-50 rounded px-3 py-2 text-sm focus:border-signal-500 focus:outline-none"
                >
                    <option value={50}>Last 50</option>
                    <option value={100}>Last 100</option>
                    <option value={250}>Last 250</option>
                    <option value={500}>Last 500</option>
                </select>
            </div>

            {error ? (
                <div className="border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 rounded font-mono text-sm">
                    {(error as Error).message}
                </div>
            ) : null}

            {isLoading ? (
                <div className="font-mono text-xs text-graphite-500 uppercase tracking-widest">
                    Loading...
                </div>
            ) : filtered.length === 0 ? (
                <div className="font-mono text-xs text-graphite-500 py-8 text-center uppercase tracking-widest">
                    No activity matches your filter
                </div>
            ) : (
                <div className="border border-graphite-200 dark:border-graphite-800 rounded overflow-hidden">
                    {filtered.map((entry) => (
                        <LogRow
                            key={entry.id}
                            entry={entry}
                            expanded={expanded.has(entry.id)}
                            onToggle={() => toggleExpanded(entry.id)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function LogRow({
    entry,
    expanded,
    onToggle,
}: {
    entry: ActivityLogEntry;
    expanded: boolean;
    onToggle: () => void;
}) {
    const actionCategory = entry.action.endsWith('.delete')
        ? 'delete'
        : entry.action.endsWith('.insert')
          ? 'insert'
          : 'update';

    return (
        <button
            type="button"
            onClick={onToggle}
            className={cn(
                'w-full text-left px-4 py-3 border-b border-graphite-200 dark:border-graphite-900 last:border-b-0 transition-colors',
                'hover:bg-graphite-100/60 dark:hover:bg-graphite-900/60',
                expanded && 'bg-graphite-100/40 dark:bg-graphite-900/40'
            )}
        >
            <div className="flex items-center gap-3">
                <div
                    className={cn(
                        'shrink-0 w-1 h-10 rounded-sm',
                        actionCategory === 'insert' && 'bg-done-500',
                        actionCategory === 'update' && 'bg-signal-500',
                        actionCategory === 'delete' && 'bg-amber-500'
                    )}
                />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-graphite-900 dark:text-graphite-100">{entry.actor_email}</span>
                        <span className="font-mono text-[10px] uppercase tracking-widest text-graphite-500">
                            {entry.actor_role}
                        </span>
                        <span className="text-graphite-600 dark:text-graphite-400">·</span>
                        <span className="font-mono text-graphite-700 dark:text-graphite-300">{entry.action}</span>
                    </div>
                    <div className="font-mono text-[11px] text-graphite-500 tabular">
                        {formatTimestamp(entry.created_at)}
                    </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-graphite-500">
                    {expanded ? '−' : '+'}
                </div>
            </div>

            {expanded ? (
                <div className="mt-3 pl-4 text-xs">
                    <DiffView oldValue={entry.old_value} newValue={entry.new_value} />
                </div>
            ) : null}
        </button>
    );
}

function DiffView({
    oldValue,
    newValue,
}: {
    oldValue: Record<string, unknown> | null;
    newValue: Record<string, unknown> | null;
}) {
    // Build a list of changed keys. For inserts oldValue is null, show all new keys.
    // For updates, show only changed keys. For deletes, show all old keys.
    const allKeys = Array.from(
        new Set([...(oldValue ? Object.keys(oldValue) : []), ...(newValue ? Object.keys(newValue) : [])])
    ).filter((k) => k !== 'updated_at' && k !== 'created_at');

    const changes = allKeys
        .map((key) => ({
            key,
            before: oldValue?.[key],
            after: newValue?.[key],
        }))
        .filter(({ before, after }) => JSON.stringify(before) !== JSON.stringify(after));

    if (changes.length === 0) {
        return <div className="text-graphite-400 dark:text-graphite-600 font-mono">No field changes recorded.</div>;
    }

    return (
        <table className="w-full font-mono tabular">
            <tbody>
                {changes.map(({ key, before, after }) => (
                    <tr key={key} className="border-b border-graphite-200 dark:border-graphite-900 last:border-b-0">
                        <td className="py-1 pr-4 text-graphite-500 uppercase tracking-wider text-[10px] align-top">
                            {key}
                        </td>
                        <td className="py-1 pr-4 text-graphite-500 align-top">
                            {formatValue(before)}
                        </td>
                        <td className="py-1 pl-2 text-graphite-600 dark:text-graphite-400 align-top">→</td>
                        <td className="py-1 pl-2 text-graphite-900 dark:text-graphite-100 align-top">
                            {formatValue(after)}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function formatValue(v: unknown): string {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    return JSON.stringify(v);
}
